import { useState } from 'react';
import useOnlyDevelopers from '../../../../../../../state/setOnlyDevelopers';
import { NudosGenericModal, NudosModalContent } from '../../../../../../../components/NudosComponents';
import CustomModalContentSendProducts from './components/CustomModalContentSendProducts';
import {
  ISendProductsToWarehouse,
  IShipmentData,
  IproductReferenceDataInCountry,
  SHIPMENT_STATUS_ID
} from '../../../../../../../types/orders';
import { sendProductsToWarehouse } from '../../../../../../../services/orders';
import { massUpdateShipmentStatus } from '../../../../../../../services/traking.services';
import { IMassiveDeliveryInProgress } from '../../../../../../../types/traking';
import { extractProductData } from '../../../../../../../utils/extractProductData';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import {
  displayConfigurationTransitionBlockedNotification,
  evaluateShipmentTransitToDestinationTransition,
  handleConfigurationTransitionBlockedApiError,
  SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
} from '../../../../../../../utils/productConfigurationSummary.utils';
import ShipmentStatusTransitionModal from '../../../../../Components/ShipmentStatusTransitionModal/ShipmentStatusTransitionModal';
import { TRANSIT_TO_DESTINATION_TO_DELIVERED_CONFIG } from '../../../Tab4Address/Components/ShipmentCard/useShipmentCardTracking';
import { TOOL_STATUS_NAME } from '../../../../../../../types/tools';

interface SendProductsToWarehouseModalProps {
  changeShowModal: () => void;
  orderId: number;
  organizationName?: string;
  shipmentPlace?: string;
  shipmentFlag?: string;
  nameCountry?: string;
  listOfGroupedShipments?: IShipmentData[];
  updateWithLoaders: () => void;
  references?: IproductReferenceDataInCountry[];
}
const SendProductsToWarehouseModal = ({
  changeShowModal,
  orderId,
  organizationName,
  shipmentPlace = '',
  shipmentFlag,
  nameCountry,
  listOfGroupedShipments,
  updateWithLoaders,
  references
}: SendProductsToWarehouseModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [shipmentsForDeliveryModal, setShipmentsForDeliveryModal] = useState<IShipmentData[] | null>(null);
  const { isOnlyDevsLogistics } = useOnlyDevelopers();

  const closeModal = () => changeShowModal?.();

  const closeDeliveryModalAndMain = () => {
    setShipmentsForDeliveryModal(null);
    displaySuccessNotification({ customJSXMessage: <>El destino del envío se ha modificado correctamente</> });
    closeModal();
    updateWithLoaders();
  };

  const handleDeliveryModalClose = (action: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof action === 'function' ? action(true) : action;
    if (next === false) closeDeliveryModalAndMain();
  };

  const handleDeliveryTransitionConfirmed = () => {
    updateWithLoaders();
    setShipmentsForDeliveryModal(null);
    closeModal();
  };

  const updateStatusAndOpenDeliveryModal = async (): Promise<boolean> => {
    const shipmentsInWarehouseFlowStates = (listOfGroupedShipments ?? []).filter(
      s =>
        s.statusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE ||
        s.statusId === SHIPMENT_STATUS_ID.WAREHOUSE_STOP
    );

    if (!shipmentsInWarehouseFlowStates.length) return false;

    if (isOnlyDevsLogistics) {
      for (const shipment of shipmentsInWarehouseFlowStates) {
        const { isBlocked } = await evaluateShipmentTransitToDestinationTransition(shipment.shipmentId);

        if (isBlocked) {
          displayConfigurationTransitionBlockedNotification({
            displayErrorNotification,
            mainMessage: SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
          });
          return false;
        }
      }
    }

    const bodyMassive: IMassiveDeliveryInProgress[] = shipmentsInWarehouseFlowStates.map(shipment => ({
      shipmentId: shipment.shipmentId,
      newSerials: extractProductData(shipment?.products ?? [])
    }));

    await massUpdateShipmentStatus(SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION, bodyMassive);

    const shipmentsThatWereInWarehouse = shipmentsInWarehouseFlowStates.filter(
      s => s.statusId === SHIPMENT_STATUS_ID.WAREHOUSE_STOP
    );

    if (!shipmentsThatWereInWarehouse.length) return false;

    const shipmentsAsTransitToDestination: IShipmentData[] = shipmentsThatWereInWarehouse.map(s => ({
      ...s,
      statusId: SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION
    }));

    setShipmentsForDeliveryModal(shipmentsAsTransitToDestination);
    setLoading(false);

    return true;
  };

  const sendDataProductsToWarehouse = async () => {
    if (!listOfGroupedShipments || listOfGroupedShipments.length === 0) return;
    setLoading(true);
    try {
      const dataBody: ISendProductsToWarehouse = {
        shipmentGroupId: listOfGroupedShipments[0].shipmentGroupId
      };

      await sendProductsToWarehouse(dataBody);

      const deliveryModalOpened = await updateStatusAndOpenDeliveryModal();
      if (deliveryModalOpened) return;

      displaySuccessNotification({ customJSXMessage: <>El destino del envío se ha modificado correctamente</> });
      closeModal();
      updateWithLoaders();
    } catch (error) {
      const handledConfigurationBlock =
        isOnlyDevsLogistics &&
        handleConfigurationTransitionBlockedApiError(
          error,
          SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE,
          displayErrorNotification
        );

      if (!handledConfigurationBlock) {
        displayErrorNotification();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!!shipmentsForDeliveryModal?.length) {
    return (
      <ShipmentStatusTransitionModal
        setShowModal={handleDeliveryModalClose}
        transitionConfig={TRANSIT_TO_DESTINATION_TO_DELIVERED_CONFIG}
        updateWithoutLoaders={handleDeliveryTransitionConfirmed}
        orderId={String(orderId)}
        organizationName={organizationName}
        receiverName={TOOL_STATUS_NAME.WAREHOUSE}
        selectedShipments={shipmentsForDeliveryModal}
        references={references}
      />
    );
  }

  return (
    <NudosGenericModal
      closeModalCallback={closeModal}
      clickOutsideCallback={closeModal}
      customClassName="sendProductsToWarehouseModal"
      modalContent={
        <NudosModalContent
          customWidth="580px"
          customHeight="480px"
          modalContentTitle="Enviar productos a la Bodega"
          customHeaderClass="sendProductsToWarehouseModalHeader"
          toCloseModal={closeModal}
          CustomModalComponent={
            <CustomModalContentSendProducts
              orderId={orderId}
              organizationName={organizationName}
              shipmentPlace={shipmentPlace}
              shipmentFlag={shipmentFlag}
              nameCountry={nameCountry}
              listOfGroupedShipments={listOfGroupedShipments}
              loading={loading}
              sendDataProductsToWarehouse={sendDataProductsToWarehouse}
            />
          }
        />
      }
    />
  );
};

export default SendProductsToWarehouseModal;
